import React, { useState } from "react";
import { Menu as MenuIcon, MessageSquareText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FeedbackModal } from "@/components/FeedbackModal";
import { ShareButton } from "@/components/ShareDialog";
import { PortalUserMenu } from "@/components/PortalUserMenu";
import { getImpersonation, exitImpersonation } from "@/lib/impersonation";

/**
 * Shared portal top bar, modeled on the School workspace header.
 * Fixed/sticky at the top; left shows the portal context (small type label +
 * organization name); right holds Give Feedback, an optional Share button, and
 * the logged-in user menu (same styling/order/behavior as the School portal).
 * The left navigation menus stay untouched.
 */
export const PortalTopBar = ({
  label,
  orgName,
  accent = "#0b1220",
  onSignOut,
  onHamburger,
  shareEnabled = false,
  feedbackPortalType,
  badge = null,
}) => {
  const [fbOpen, setFbOpen] = useState(false);
  const imp = getImpersonation();
  return (
    <div className="sticky top-0 z-40 bg-white border-b border-[#e5e7eb]" data-testid="portal-topbar">
      <div className="px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {onHamburger && (
            <button
              type="button"
              onClick={onHamburger}
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-[#e5e7eb] text-[#111827] shrink-0"
              aria-label="Open menu"
              data-testid="portal-topbar-hamburger"
            >
              <MenuIcon className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold" data-testid="portal-topbar-label">
              {label}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[15px] sm:text-[16px] font-bold text-[#111827] truncate" data-testid="portal-topbar-org">
                {orgName || "—"}
              </div>
              {badge}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {imp.active && (
            <Button
              variant="outline" size="sm"
              className="inline-flex bg-white border-[#d1d5db] text-[#111827] hover:bg-[#f9fafb] h-10 rounded-[10px]"
              onClick={exitImpersonation}
              data-testid="portal-topbar-return-admin"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> {imp.isDemo ? "Return to Admin" : "Exit Preview"}
            </Button>
          )}
          <Button
            variant="outline" size="sm"
            className="hidden sm:inline-flex bg-white border-[#d1d5db] text-[#111827] hover:bg-[#f9fafb] h-10 rounded-[10px]"
            onClick={() => setFbOpen(true)}
            data-testid="portal-topbar-feedback"
          >
            <MessageSquareText className="h-4 w-4 mr-1.5" /> Give Feedback
          </Button>
          {shareEnabled && <ShareButton className="hidden sm:inline-flex" />}
          <PortalUserMenu accent={accent} onSignOut={onSignOut} placement="topbar" />
        </div>
      </div>
      <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} portalType={feedbackPortalType} />
    </div>
  );
};

export default PortalTopBar;
